import { createElement } from 'react';
import type { ListTableGroup, ListTableProps, ListTableRow } from 'framework/ui/ui-builder';
import type { FilterBarProps } from '../../components/FilterBar';
import type { CollectionViewProps } from './CollectionView';
import CollectionContentLayout from './CollectionContentLayout';
import type { CollectionPager } from '../../hooks/collection/useCollectionPager';

type Pager<Row extends ListTableRow> = CollectionPager<Row, unknown>;

type OptionalControls<Controls> = Controls | { [Key in keyof Controls]?: never };

export type CollectionBindingState<Row extends ListTableRow> = Pick<
  Pager<Row>,
  'isLoading' | 'error' | 'totalElements'
> &
  Pick<CollectionViewProps<Row>, 'retry'> &
  OptionalControls<Pick<Pager<Row>, 'sort' | 'setSort'>> &
  OptionalControls<Pick<Pager<Row>, 'searchValue' | 'setSearchValue' | 'appliedSearchValue'>> &
  (
    | ({ rows: Row[]; groups?: never } & OptionalControls<
        Pick<Pager<Row>, 'hasMore' | 'isLoadingMore' | 'loadMore' | 'loadMoreError'>
      >)
    | { groups: ListTableGroup<Row>[]; rows?: never }
  );

type BoundListProp =
  | 'rows'
  | 'groups'
  | 'sort'
  | 'onSortChange'
  | 'hasMore'
  | 'isLoadingMore'
  | 'loadMore'
  | 'loadMoreError'
  | 'retryLoadMore';

type CollectionTableOptions<Row extends ListTableRow> = Omit<ListTableProps<Row>, BoundListProp>;

interface CollectionBinding<Row extends ListTableRow> {
  pageProps: { loading: boolean; filterBarProps: FilterBarProps; contentLayout: React.JSX.Element };
  contentProps: CollectionViewProps<Row>;
}

/** Maps collection state to UI props without fetching, debouncing or rendering a FilterBar. */
const bindCollection = <Row extends ListTableRow>(
  collection: CollectionBindingState<Row>,
  tableOptions: CollectionTableOptions<NoInfer<Row>>,
  filterBarProps: Omit<FilterBarProps, 'totalRecords'> = {},
): CollectionBinding<Row> => {
  // Existing table-config factories may include data/control props. Only appearance and actions belong here.
  const {
    rows,
    groups,
    sort,
    onSortChange,
    hasMore,
    isLoadingMore,
    loadMore,
    loadMoreError,
    retryLoadMore,
    ...tableProps
  }: Partial<ListTableProps<Row>> = tableOptions;
  const listProps: CollectionViewProps<Row>['listProps'] = collection.groups
    ? { ...tableProps, columns: tableOptions.columns, groups: collection.groups }
    : {
        ...tableProps,
        columns: tableOptions.columns,
        rows: collection.rows,
        hasMore: collection.hasMore,
        isLoadingMore: collection.isLoadingMore,
        loadMore: collection.loadMore,
        loadMoreError: collection.loadMoreError,
      };

  const contentProps: CollectionViewProps<Row> = {
    loading: collection.isLoading,
    error: collection.error,
    retry: collection.retry,
    highlightQuery: collection.appliedSearchValue,
    listProps: { ...listProps, sort: collection.sort, onSortChange: collection.setSort },
  };

  const resolvedFilterBarProps: FilterBarProps = {
    ...filterBarProps,
    searchProps:
      filterBarProps.searchProps && collection.setSearchValue
        ? { ...filterBarProps.searchProps, value: collection.searchValue, onChange: collection.setSearchValue }
        : filterBarProps.searchProps,
    totalRecords: collection.totalElements,
  };

  return {
    pageProps: {
      loading: collection.isLoading,
      filterBarProps: resolvedFilterBarProps,
      contentLayout: createElement(CollectionContentLayout<Row>, {
        ...contentProps,
        filterBarProps: resolvedFilterBarProps,
      }),
    },
    contentProps,
  };
};

export default bindCollection;
